'use client'
import React, { useImperativeHandle, useRef } from 'react'
import { Heading, Picture, Text, Button, Link, Container } from '../../components/index.js'
import { withLayout } from '../../shared/Styles.js'
import type {
  ActivityMechanismProps,
  ActivityMechanismItem,
  ActivityMechanismSemanticName,
  ResponsiveBackgroundImage,
} from './types.js'
import { cn } from '../../helpers/utils.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { cva, type VariantProps } from 'class-variance-authority'
import { getLocalizedPath } from '../../helpers/index.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

const componentType = 'image'
const componentName = 'activity_mechanism'

/**
 * 标题样式变体
 */
const titleVariants = cva('line-clamp-2', {
  variants: {
    theme: {
      light: 'text-[#080A0F]',
      dark: 'text-white',
    },
  },
  defaultVariants: {
    theme: 'light',
  },
})

/**
 * 描述样式变体
 */
const descriptionVariants = cva(
  'desktop:text-[16px] mt-1 line-clamp-2 text-[14px] font-bold leading-[1.4] tracking-[-0.02em]',
  {
    variants: {
      theme: {
        light: 'text-[#080A0F]',
        dark: 'text-white/80',
      },
    },
    defaultVariants: {
      theme: 'light',
    },
  }
)

/**
 * 链接样式变体
 */
const linkVariants = cva('mt-2 !p-0 text-[14px]', {
  variants: {
    theme: {
      light: 'text-[#080A0F]',
      dark: 'text-white',
    },
  },
  defaultVariants: {
    theme: 'light',
  },
})

type ThemeVariants = VariantProps<typeof titleVariants>

/**
 * 断点配置（与 Tailwind 断点对应）
 * Picture 组件使用 max-width 媒体查询，所以需要按从小到大的顺序排列
 */
const BREAKPOINTS = {
  default: 767, // <768px (移动端)
  tablet: 1024, // 768px - 1024px
  laptop: 1439, // 1025px - 1439px
  desktop: 1919, // 1440px - 1919px
  lgDesktop: 9999, // ≥1920px (最大值，作为默认图片)
} as const

/**
 * 将响应式背景图片对象转换为 Picture 组件的 source 字符串
 * 格式: "url1 767, url2 1024, url3 1439, url4 1919, url5"
 */
const buildResponsiveSource = (backgroundImage?: ResponsiveBackgroundImage): string => {
  if (!backgroundImage) return ''

  const sources: string[] = []

  // 按断点顺序添加图片 URL
  // lgDesktop 作为默认图片，不需要断点值
  if (backgroundImage.lgDesktop?.url) {
    sources.push(`${backgroundImage.lgDesktop.url}`)
  }
  if (backgroundImage.desktop?.url) {
    sources.push(`${backgroundImage.desktop.url} ${BREAKPOINTS.desktop}`)
  }
  if (backgroundImage.laptop?.url) {
    sources.push(`${backgroundImage.laptop.url} ${BREAKPOINTS.laptop}`)
  }
  if (backgroundImage.tablet?.url) {
    sources.push(`${backgroundImage.tablet.url} ${BREAKPOINTS.tablet}`)
  }
  if (backgroundImage.default?.url) {
    sources.push(`${backgroundImage.default.url} ${BREAKPOINTS.default}`)
  }

  return sources.join(', ')
}

/**
 * 获取响应式背景图片的默认 alt 文本
 */
const getDefaultAlt = (backgroundImage?: ResponsiveBackgroundImage): string => {
  if (!backgroundImage) return ''
  return (
    backgroundImage.default?.alt ||
    backgroundImage.tablet?.alt ||
    backgroundImage.laptop?.alt ||
    backgroundImage.desktop?.alt ||
    backgroundImage.lgDesktop?.alt ||
    ''
  )
}

/**
 * 单个活动机制卡片组件
 */
const ActivityMechanismCard: React.FC<{
  item: ActivityMechanismItem
  theme?: ThemeVariants['theme']
  classNames?: Partial<Record<ActivityMechanismSemanticName, string>>
}> = ({ item, theme, classNames }) => {
  const { title, description, linkText, linkHref, backgroundImage } = item
  const responsiveSource = buildResponsiveSource(backgroundImage)
  const defaultAlt = getDefaultAlt(backgroundImage)
  const { locale = 'us' } = useAiuiContext()

  return (
    <div
      className={cn(
        'rounded-card relative flex flex-col overflow-hidden',
        'desktop:px-6 h-[280px] p-4',
        'laptop:gap-4 gap-3',
        'laptop:h-[320px] lg-desktop:[400px]',
        classNames?.card
      )}
      data-headless-type-name={`${componentType}#${componentName}`}
    >
      <Link
        asChild={!linkHref}
        href={getLocalizedPath(trackUrlRef(linkHref, `${componentType}_${componentName}`), locale)}
        className="no-underline"
      >
        {/* 响应式背景图片 */}
        {responsiveSource && (
          <Picture
            source={responsiveSource}
            alt={defaultAlt || title}
            className={cn('absolute inset-0 size-full', classNames?.background)}
            imgClassName="size-full object-cover"
          />
        )}

        {/* 文本内容区域 */}
        <div className="relative z-10">
          <Heading as="h3" size={3} className={cn(titleVariants({ theme }), classNames?.title)}>
            {title}
          </Heading>
          <Text as="p" className={cn(descriptionVariants({ theme }), classNames?.description)}>
            {description}
          </Text>
          {linkText && linkHref && (
            <Button
              variant="link"
              as="a"
              href={getLocalizedPath(trackUrlRef(linkHref, `${componentType}_${componentName}`), locale)}
              className={cn(linkVariants({ theme }), classNames?.link)}
              data-headless-title-desc-button={`${title}#${description}#${linkText}`}
            >
              {linkText}
            </Button>
          )}
        </div>
      </Link>
    </div>
  )
}

/**
 * ActivityMechanism - 活动机制组件
 *
 * @description 展示活动机制/会员权益等卡片列表的组件，支持响应式布局和主题切换
 */
const ActivityMechanism = React.forwardRef<HTMLDivElement, ActivityMechanismProps>(({ data, classNames }, ref) => {
  const { items = [], theme = 'light' } = data

  const innerRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

  useExposure(innerRef, {
    componentType,
    componentName,
    componentTitle: items[0]?.title || '',
    componentDescription: items[0]?.description || '',
  })

  if (!items || items.length === 0) {
    return null
  }

  // 根据 items 数量映射 laptop/desktop/lg-desktop 的列数类名
  // 使用显式类名映射以确保 Tailwind 能正确扫描
  const gridColsMap: Record<number, string> = {
    1: 'laptop:grid-cols-1 desktop:grid-cols-1 lg-desktop:grid-cols-1',
    2: 'laptop:grid-cols-2 desktop:grid-cols-2 lg-desktop:grid-cols-2',
    3: 'tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-3 lg-desktop:grid-cols-3',
    4: 'laptop:grid-cols-4 desktop:grid-cols-4 lg-desktop:grid-cols-4',
  }
  const gridColsClass = gridColsMap[Math.min(items.length, 4)] || gridColsMap[4]

  return (
    <Container
      data-ui-component-id="ActivityMechanism"
      className={cn('activity-mechanism', 'w-full', classNames?.root)}
      ref={innerRef}
    >
      <div className={cn('tablet:grid-cols-2 laptop:gap-4 grid grid-cols-1 gap-3', gridColsClass, classNames?.grid)}>
        {items.map((item, index) => (
          <ActivityMechanismCard
            key={`${item.title}-${index}`}
            item={item}
            theme={theme}
            classNames={{
              ...classNames,
              ...(items?.length === 3 && {
                card: classNames?.card + ' ' + 'first:tablet:col-span-full first:laptop:col-span-1',
              }),
            }}
          />
        ))}
      </div>
    </Container>
  )
})

ActivityMechanism.displayName = 'ActivityMechanism'

export default ActivityMechanism
